import { useEffect, useState } from "react";
import { useSearchParams, Link } from "react-router-dom";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { CheckCircle2, XCircle, Clock } from "lucide-react";

export default function PaymentSuccess() {
  const [params] = useSearchParams();
  const sessionId = params.get("session_id");
  const { refresh } = useAuth();
  const [state, setState] = useState({ status: "checking", payment_status: "", kind: "" });

  useEffect(() => {
    if (!sessionId) { setState({ status: "error", payment_status: "" }); return; }
    let attempts = 0;
    let cancelled = false;
    const poll = async () => {
      if (cancelled) return;
      try {
        const { data } = await api.get(`/payments/checkout/status/${sessionId}`);
        if (data.payment_status === "paid") {
          if (data.kind === "signup") await refresh();
          setState({ status: "paid", ...data });
          return;
        }
        if (data.status === "expired") { setState({ status: "expired", ...data }); return; }
        attempts += 1;
        if (attempts >= 8) { setState({ status: "timeout", ...data }); return; }
        setTimeout(poll, 2000);
      } catch {
        setState({ status: "error", payment_status: "" });
      }
    };
    poll();
    return () => { cancelled = true; };
  }, [sessionId, refresh]);

  const isSignup = state.kind === "signup";
  const paidTitle = isSignup ? "Account activated." : "Payment received.";
  const paidMsg = isSignup
    ? "Welcome to AddVendTour — your account is now active. Time to explore."
    : "Your booth is confirmed. The organizer has been notified.";

  return (
    <div className="mx-auto max-w-xl px-6 py-24 text-center">
      {state.status === "checking" && (
        <>
          <Clock className="h-14 w-14 mx-auto" style={{ color: "var(--terracotta)" }} />
          <h1 className="font-serif text-4xl mt-6">Checking your payment…</h1>
          <p className="mt-3 text-sm" style={{ color: "var(--ink-soft)" }}>Hang tight — this only takes a moment.</p>
        </>
      )}
      {state.status === "paid" && (
        <>
          <CheckCircle2 className="h-14 w-14 mx-auto" style={{ color: "var(--moss)" }} data-testid="payment-success-icon" />
          <h1 className="font-serif text-4xl mt-6" data-testid="payment-success-title">{paidTitle}</h1>
          <p className="mt-3 text-sm" style={{ color: "var(--ink-soft)" }}>{paidMsg}</p>
          <Link to={isSignup ? "/events" : "/vendor/dashboard"} data-testid="payment-continue" className="mt-8 inline-block btn-primary px-6 py-3 rounded-full">
            {isSignup ? "Explore events" : "Back to dashboard"}
          </Link>
        </>
      )}
      {(state.status === "expired" || state.status === "timeout" || state.status === "error") && (
        <>
          <XCircle className="h-14 w-14 mx-auto" style={{ color: "var(--terracotta)" }} />
          <h1 className="font-serif text-4xl mt-6">Payment not confirmed.</h1>
          <p className="mt-3 text-sm" style={{ color: "var(--ink-soft)" }}>Please try again from your dashboard.</p>
          <Link to="/activate" className="mt-8 inline-block btn-primary px-6 py-3 rounded-full">Back to activation</Link>
        </>
      )}
    </div>
  );
}

